import { useCallback, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { DialogController } from "../../../../utils/hooks/use-dialog";
import type { SegmentCrudDialogState } from "./hooks/use-segment-crud-datagrid-dialog";

import { Alert, Dialog, LinearProgress } from "@mui/material";
import toast from "react-hot-toast";
import { useSupabaseLoader } from "../../../../server/supabase/hooks";
import { uuidv4, type UUID } from "../../../../utils/uuid";
import { SupabaseSegmentService } from "../../server/supabase-segment-service";
import { makeDefaultSegment } from "../../types";
import { NewSegmentManagerWrapper } from "../manager-v2/segment-manager-wrapper";
import { useSegmentCrudFunctions } from "./hooks/use-segment-crud-functions";
import { ArchiveDialog } from "./segment-modal/archive-dialog";
import {
  TabbedEditSegmentModal,
  type EditModalTab,
} from "./segment-modal/tabbed-edit-segment-modal/tabbed-edit-segment-modal";
import { UnarchiveWithNewNameModal } from "./segment-modal/unarchive-segment-modal";
import type { SegmentRowWithOptions } from "./types";

export const SegmentCrudDataGridDialogV2: FC<{
  dialog: DialogController<SegmentCrudDialogState>;
  is_recipient_type: boolean;
  reload: () => void;
  processedSegments: SegmentRowWithOptions[];
}> = ({ dialog, is_recipient_type, reload, processedSegments }) => {
  const { t } = useTranslation();

  const { handleDelete, handleUnarchive, handleUnarchiveWithNewName } = useSegmentCrudFunctions({
    is_recipient_type,
    reload,
    onUnarchiveDuplicate: (data) => dialog.handleOpen({ type: "unarchiveWithNewName", data }),
  });

  const handleClose = useCallback(
    (saved: boolean) => {
      if (saved) {
        dialog.handleClose();
        toast.success(t("Segment saved successfully"));
        reload();
      } else {
        dialog.handleClose();
      }
    },
    [dialog, reload, t],
  );

  if (!dialog.data || !dialog.open) return null;

  const { type, data } = dialog.data;

  switch (type) {
    case "delete":
      return (
        <ArchiveDialog
          title={t("Archive Segment")}
          content={t("Are you sure you wish to archive this Segment?")}
          open={true}
          onCancel={() => {
            dialog.handleClose();
            data.resolve(false);
          }}
          onConfirm={async () => {
            dialog.handleClose();
            data.resolve((await handleDelete(data.segment_id)) ?? false);
          }}
        />
      );

    case "unarchive":
      return (
        <ArchiveDialog
          title={t("Unarchive Segment")}
          content={t("Are you sure you wish to unarchive this Segment?")}
          open={true}
          onCancel={() => dialog.handleClose()}
          onConfirm={async () => {
            await handleUnarchive(data.segment_id, data.segment_name);
            dialog.handleClose();
          }}
        />
      );
    case "unarchiveWithNewName":
      return (
        <UnarchiveWithNewNameModal
          open={true}
          onCancel={() => dialog.handleClose()}
          segmentId={data.segment_id}
          segmentName={data.segment_name}
          onSave={async (segment) => {
            const result = await handleUnarchiveWithNewName(segment);
            if (!result) return;

            if (result?.success) dialog.handleClose();

            return !result.success && result.errors ? result.errors : undefined;
          }}
        />
      );
    case "clone":
      if (!data?.segment_id) return null;
      return (
        <SegmentCrudDataGridDialogV2Clone
          onClose={handleClose}
          segment_id={data.segment_id}
          disabled={false}
          is_recipient_type={is_recipient_type}
        />
      );
    case "add":
      return (
        <SegmentCrudDataGridDialogV2New
          is_recipient_type={is_recipient_type}
          onClose={handleClose}
          nameRequired={data?.nameRequired}
        />
      );
    case "edit":
      return data?.segment_id ? (
        <SegmentCrudDataGridDialogV2Edit
          is_recipient_type={is_recipient_type}
          onClose={handleClose}
          segment_id={data.segment_id}
          disabled={
            (processedSegments
              .find((x) => x.id === data.segment_id)
              ?.moments.filter(({ finished }) => !finished).length ?? 0) > 1
          }
        />
      ) : null;
  }
};

export const SegmentCrudDataGridDialogV2New: FC<{
  is_recipient_type: boolean;
  onClose: (saved: boolean) => void;
  open?: boolean;
  onSave?: (id: UUID | null) => void;
  nameRequired?: boolean;
}> = ({ is_recipient_type, onClose, onSave, open = true, nameRequired = true }) => {
  const { data, loading, error } = useSupabaseLoader(
    async ({ supabase }) => {
      if (is_recipient_type) return { ...makeDefaultSegment(uuidv4()), is_recipient_type };

      const service = new SupabaseSegmentService(supabase);
      const resp = await service.getDefaultSegment();

      if (resp.error) throw error;

      if (!resp.data) return { ...makeDefaultSegment(uuidv4()), is_recipient_type };

      return { ...service.cloneSegment(resp.data, uuidv4()), description: "" };
    },
    [is_recipient_type],
  );

  if (loading) return <LinearProgress />;

  return (
    <Dialog open={open} fullWidth maxWidth="lg" onClose={() => onClose(false)}>
      {loading && <LinearProgress />}
      {error && <Alert severity="error">Unable to load Segment data</Alert>}
      {data && (
        <NewSegmentManagerWrapper
          segment={data}
          is_recipient_type={is_recipient_type}
          disabled={false}
          onClose={onClose}
          nameRequired={nameRequired}
          onSave={onSave}
        />
      )}
    </Dialog>
  );
};

export const SegmentCrudDataGridDialogV2Edit: FC<{
  is_recipient_type: boolean;
  onClose: (saved: boolean) => void;
  segment_id: UUID;
  disabled: boolean;
  nameRequired?: boolean;
}> = ({ is_recipient_type, onClose, segment_id, disabled, nameRequired }) => {
  const [tab, setTab] = useState<EditModalTab>("DETAILS");

  const { data, error, loading } = useSupabaseLoader(
    async ({ supabase }) => {
      const resp = await new SupabaseSegmentService(supabase).getSegmentData(segment_id);

      if (resp.error) throw resp.error;

      return resp.data;
    },
    [segment_id],
  );

  return (
    <Dialog open={true} fullWidth maxWidth="lg" onClose={() => onClose(false)}>
      {loading && <LinearProgress />}
      {error && <Alert severity="error">Unable to load Segment data</Alert>}
      {data && (
        <NewSegmentManagerWrapper
          segment={data}
          disabled={disabled}
          is_recipient_type={is_recipient_type}
          onClose={onClose}
          nameRequired={nameRequired}
          slots={{
            ContentWrapper: ({ children }) => (
              <TabbedEditSegmentModal
                segment={data}
                disabled={disabled}
                tab={tab}
                setTab={setTab}
                include_recipients_tab={false}
              >
                {children}
              </TabbedEditSegmentModal>
            ),
          }}
        />
      )}
    </Dialog>
  );
};

const SegmentCrudDataGridDialogV2Clone: FC<{
  onClose: (saved: boolean) => void;
  segment_id: UUID;
  disabled: boolean;
  is_recipient_type: boolean;
}> = ({ segment_id, onClose, disabled, is_recipient_type }) => {
  const { data, loading, error } = useSupabaseLoader(
    async ({ supabase }) => {
      if (!segment_id) return;

      const service = new SupabaseSegmentService(supabase);
      const resp = await service.getSegmentData(segment_id);

      if (resp.error || !resp.data) throw resp.error;

      return service.cloneSegment(resp.data, uuidv4());
    },
    [segment_id],
  );

  return (
    <Dialog open={true} fullWidth maxWidth="lg" onClose={() => onClose(false)}>
      {loading && <LinearProgress />}
      {error && <Alert severity="error">Unable to load Segment data</Alert>}
      {data && (
        <NewSegmentManagerWrapper
          segment={data}
          disabled={disabled}
          is_recipient_type={is_recipient_type}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};
